import React, { useContext, useEffect, useRef } from "react";
import LAView from "../LAView";
import LAButton from "../LAButton";
import { IMAGES, SVG_IMAGES } from "../../assets";
import LAText from "../LAText";
import { ThemeContext } from "../../context/ThemeProvider";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFont, faHashtag } from '@fortawesome/free-solid-svg-icons';
import { createLogger } from "../../utils/logger";
import { AuthContext } from "../../context/AuthProvider";

const GlossaryFilter = (props) => {
  const { loggingEnabled } = useContext(AuthContext);
  const logger = createLogger(loggingEnabled, 'GlossaryFilter');
  const selectedValue = props?.selectedValue;
  const alpha = Array.from(Array(26)).map((e, i) => i + 65);
  const alphabet = alpha.map((x) => String.fromCharCode(x));
  const alphabetAndNumbers = ["#"].concat(alphabet);
  const alphabetAndNumbersAndAll = alphabetAndNumbers.concat("ALL");

  const ref = useRef();
  const { theme, currentTheme } = useContext(ThemeContext);
  
  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      logger.log("Click detected, checking if outside:", {
        refExists: !!ref.current,
        target: e.target,
        contains: ref.current?.contains(e.target)
      });
      
      if (ref.current && !ref.current.contains(e.target)) {
        logger.log("Click outside detected, closing modal");
        props.onCancel();
      }
    };
    
    // Add a small delay before attaching the event listener
    const timer = setTimeout(() => {
      logger.log("Attaching click listener");
      document.addEventListener("click", checkIfClickedOutside);
    }, 100);
    
    return () => {
      logger.log("Cleaning up click listener");
      clearTimeout(timer);
      document.removeEventListener("click", checkIfClickedOutside);
    };
  }, [props.onCancel]);

  useEffect(() => {
    logger.log("Setting body overflow to hidden");
    document.body.style.overflow = "hidden";
    return () => {
      logger.log("Resetting body overflow");
      document.body.style.overflow = "auto";
    };
  }, []);

  // Helper function to get icon background color
  const getIconBg = () => {
    return props?.iconBg || (currentTheme === 'light' ? theme?.icon_bg?.study_light : theme?.icon_bg?.study_dark);
  }

  // Helper function for text color
  const getTextColor = () => {
    return currentTheme === 'light' 
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        duration: 0.2,
        type: "spring",
        damping: 25,
        stiffness: 500
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9,
      transition: { duration: 0.15 }
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, transition: { duration: 0.15 } }
  };

  const getButtonStyle = (letter) => {
    const isSelected = letter === selectedValue;
    
    if (isSelected) {
      return {
        backgroundColor: props?.iconBg,
        color: '#ffffff',
        transform: 'scale(1.05)',
        boxShadow: '0px 4px 12px rgba(0,0,0,0.1)'
      };
    } else {
      return {
        backgroundColor: currentTheme === 'light' 
          ? theme?.option?.unselected?.light
          : theme?.option?.unselected?.dark,
        color: getTextColor(),
        border: `1px solid ${currentTheme === 'light' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`
      };
    }
  };
  const getCardBackground = () => {
    const baseColor = theme?.homeCard?.start || '#ffffff';
    const darkerShade = theme?.homeCard?.end || '#f5f5f5';
    return {
      background: `linear-gradient(to right, ${baseColor}, ${darkerShade})`,
    };
  };
  
  const getOptionColor = (selected) => {
    if (selected) {
      return currentTheme === 'light' 
        ? theme?.option?.selected?.light 
        : theme?.option?.selected?.dark;
    }
    return currentTheme === 'light' 
      ? theme?.option?.unselected?.light 
      : theme?.option?.unselected?.dark;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={overlayVariants}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={(e) => {
          logger.log("Overlay clicked");
          e.stopPropagation();
        }}
      >
        <motion.div
          ref={ref}
          variants={modalVariants}
          className="bg-white dark:bg-gray-100 rounded-xl shadow-lg w-full max-w-lg overflow-hidden relative z-[10000]"
          style={getCardBackground()}
          onClick={(e) => {
            logger.log("Modal content clicked");
            e.stopPropagation();
          }}
        >
          <div className={`p-5 border-b flex justify-between items-center `}
          >
            <h2 
              className="text-xl font-semibold"
              style={{ color: getTextColor() }}
            >
              Filter Glossary
            </h2>
            <button
              onClick={props.onCancel}
              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <FontAwesomeIcon 
                icon={faTimes} 
                style={{ color: getTextColor() }}
              />
            </button>
          </div>
          
          <div className="p-5">
            <div className="grid grid-cols-6 gap-3">
              {alphabetAndNumbersAndAll?.map((letter, idx) => {
                const isAll = letter === "ALL";
                
                return (
                  <motion.button
                    key={idx}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`${isAll ? 'col-span-3' : 'col-span-1'} py-3 rounded-lg font-medium transition-all duration-200`}
                    style={getButtonStyle(letter)}
                    onClick={() => props?.onSelectItem(letter)}
                  >
                    {letter}
                  </motion.button>
                );
              })}
            </div>
          </div>
          
          <div className="p-5 border-t border-gray-200 dark:border-gray-700 flex justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={props.onCancel}
              className="px-6 py-2 rounded-lg font-medium text-white"
              style={{ backgroundColor: props?.iconBg }}
            >
              Close
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GlossaryFilter;